import React from "react";
import { motion } from "framer-motion";
import Common from "../common/Common";
import { FaGithub } from "react-icons/fa";
import { HiArrowUpRight } from "react-icons/hi2";

import p1 from "../../assetsall/p1.jpg";
import p2 from "../../assetsall/p2.png";
import p3 from "../../assetsall/p3.png";
import p4 from "../../assetsall/p4.png";
import p5 from "../../assetsall/p5.png";
import p6 from "../../assetsall/p6.webp";
import mockiAi from "../../assetsall/mocki-ai.png";

const Portfolio = () => {
  const projects = [
    {
      id: 7,
      image: mockiAi,
      title: "Mocki AI",
      description:
        "AI-powered mock data generator that turns a prompt into realistic JSON APIs in seconds.",
      tags: ["Next.js", "Tailwind", "LLM/AI", "Prisma ORM"],
      github: "",
      demo: "https://mocki-aii.vercel.app",
    },
    {
      id: 5,
      image: p5,
      title: "SilentNote",
      description:
        "A minimal, distraction-free note-taking app that autosaves your thoughts as you type.",
      tags: ["React", "LocalStorage"],
      github: "https://github.com/Dipongkor-Roy/silentnote-app",
      demo: "https://silentnote-app.vercel.app",
    },
    {
      id: 6,
      image: p6,
      title: "Vidzo",
      description:
        "A modern video-sharing platform with clean playback, search and a responsive feed.",
      tags: ["React", "API"],
      github: "https://github.com/Dipongkor-Roy/vidzo-app",
      demo: "https://vidzo-app.vercel.app",
    },
    {
      id: 2,
      image: p2,
      title: "PodReseller",
      description:
        "A print-on-demand reseller marketplace with product listings, cart and checkout flow.",
      tags: ["MERN", "React", "Node.js", "MongoDB"],
      github: "https://github.com/Dipongkor-Roy/PodReseller-client",
      demo: "https://pod-reseller-client.vercel.app",
    },
    {
      id: 3,
      image: p3,
      title: "MotionMaster",
      description:
        "An animation playground showcasing scroll and gesture-driven UI built with Framer Motion.",
      tags: ["React", "Framer Motion"],
      github: "https://github.com/Dipongkor-Roy/motionmaster-frontend",
      demo: "https://motionmaster.vercel.app",
    },
    {
      id: 1,
      image: p1,
      title: "Travelly",
      description:
        "A travel agency website with destination highlights, tour packages and an inquiry form.",
      tags: ["React", "Html", "CSS", "JS"],
      github: "https://github.com/Dipongkor-Roy/travelly-travel-website",
      demo: "https://travelly-travel-website.netlify.app/",
    },

    // {
    //   id: 4,
    //   image: p4,
    //   title: "Learnable",
    //   github: "google.com",
    //   demo: "https://learnable-elearning.web.app",
    // },
  ];

  // container: staggers children so cards come in one after another
  const gridVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  // each card: simple, smooth fade + slide up
  const cardVariants = {
    hidden: { opacity: 0, y: 24 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <Common
      title="Portfolio 🗒️"
      subtitle="These are all the projects that I have worked on. Some of them I built before I gained much experience, so go gentle on them."
    >
      <motion.div
        className="grid w-full max-w-4xl gap-6 sm:grid-cols-2 lg:grid-cols-3"
        variants={gridVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
      >
        {projects.map(
          ({ id, image, title, description, tags, github, demo }, index) => (
            <motion.article
              key={id}
              custom={index}
              variants={cardVariants}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="group flex flex-col overflow-hidden rounded-xl border border-white/10 bg-white/[0.02] transition-colors duration-300 hover:border-white/25"
            >
              {/* preview */}
              <a
                href={demo}
                target="_blank"
                rel="noopener noreferrer"
                className="relative block aspect-[16/10] overflow-hidden"
              >
                <img
                  src={image}
                  alt={`${title} preview`}
                  loading="lazy"
                  className="h-full w-full object-cover object-top opacity-90 transition-all duration-500 group-hover:scale-105 group-hover:opacity-100"
                />
              </a>

              {/* body */}
              <div className="flex flex-1 flex-col gap-2 p-4">
                <h3 className="text-base font-medium text-white">{title}</h3>

                <p className="flex-1 text-xs font-light leading-relaxed text-gray-500">
                  {description}
                </p>

                {tags?.length > 0 && (
                  <p className="text-[11px] text-gray-600">
                    {tags.join(" · ")}
                  </p>
                )}

                {/* footer actions */}
                <div className="mt-3 flex items-center justify-center gap-5 border-t border-white/10 pt-3">
                  <a
                    href={demo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-gray-300 transition-colors duration-200 hover:text-rose-500"
                  >
                    Live demo
                    <HiArrowUpRight className="text-xs transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>

                  <span className="h-3.5 w-px bg-white/10" />

                  {github ? (
                    <a
                      href={github}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${title} source code on GitHub`}
                      className="text-gray-400 transition-colors duration-200 hover:text-white"
                    >
                      <FaGithub className="text-base" />
                    </a>
                  ) : (
                    <span
                      aria-label="Source code not available"
                      title="Source code not available"
                      className="cursor-not-allowed text-gray-700"
                    >
                      <FaGithub className="text-base" />
                    </span>
                  )}
                </div>
              </div>
            </motion.article>
          ),
        )}
      </motion.div>
    </Common>
  );
};

export default Portfolio;
